"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import config from '../config/config';

const WebsiteSettingsContext = createContext();

export const useWebsiteSettings = () => {
  const context = useContext(WebsiteSettingsContext);
  if (!context) {
    throw new Error('useWebsiteSettings must be used within WebsiteSettingsProvider');
  }
  return context;
};

export const WebsiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get current language from Redux
  const currentLanguage = useSelector((state) => state.language?.language || 'en');

  const fetchSettings = async (language = 'en') => {
    try {
      setLoading(true);
      
      // Fetch each section separately
      const [headerRes, footerRes, seoRes] = await Promise.all([
        fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/header?language=${language}`),
        fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/footer?language=${language}`),
        fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/seo?language=${language}`)
      ]);

      const [headerData, footerData, seoData] = await Promise.all([
        headerRes.json(),
        footerRes.json(),
        seoRes.json()
      ]);

      const combinedSettings = {
        header: headerData.success ? headerData.data : null,
        footer: footerData.success ? footerData.data : null,

        seo: seoData.success ? seoData.data : null,
      };

      setSettings(combinedSettings);
      setError(null);
    } catch (err) {
      
      setError(err.message);
      // Set default fallback settings
      setSettings({
        header: {
          logo: { url: '', alt: 'Logo', width: 60, height: 60 },
          siteName: '',
          tagline: "",
          navigationMenu: [],
        },
        footer: {
          description: '',
          officeAddress: '',
          phone: '',
          email: '',
          copyright: '© 2025. All rights reserved.',
          socialMedia: [],
          footerMenu: [],
        },
        homepage: {
          hero: { sliders: [] },
          president: { sliders: [] },
          inspiration: { sliders: [] },
          journey: { sliders: [] },
          contentSections: { enabled: true, tabs: {} },
        },
        seo: {
          defaultTitle: '',
          defaultDescription: '',
          keywords: '',
          ogImage: '',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings(currentLanguage);
  }, [currentLanguage]);

  const value = {
    settings,
    loading,
    error,
    refetch: fetchSettings,
  };

  return (
    <WebsiteSettingsContext.Provider value={value}>
      {children}
    </WebsiteSettingsContext.Provider>
  );
};
